// Status tones
// ----------------------------------------------------------------------
// Roughly forty status values flow through the grids. Rather than picking a
// colour per status, each one is assigned a *tone* describing what an operator
// should do about it. Colour then follows from the tone, so a status can never
// be coloured inconsistently between two grids.
//
// Tones also carry a dot glyph: solid for terminal states, a ring for states
// that are still moving. Colour is therefore not the only channel carrying the
// meaning, which matters both for colour-blind operators and for scanning a
// wide table at a glance.

export const TONES = {
    /** Terminal and as intended. */
    done: { color: 'success', dot: 'solid' },
    /** Terminal and not as intended. */
    failed: { color: 'error', dot: 'solid' },
    /** Still moving on its own; nothing is expected of anyone. */
    inFlight: { color: 'info', dot: 'ring' },
    /** Blocked waiting on an operator, the payer or a third party. */
    needsAction: { color: 'warning', dot: 'ring' },
    /** Informational, inactive, or a status we don't recognise. */
    neutral: { color: 'default', dot: 'solid' },
    /**
     * A classification rather than a lifecycle state. Same neutral weight as
     * `neutral` — told apart by outline instead of by colour, so categories
     * never compete with real statuses for attention.
     */
    category: { color: 'default', dot: 'solid', variant: 'outlined' }
};

// Tone assignments deliberately preserve the colour each status had before, so
// this changes how statuses look but not what they mean. The one behavioural
// change is the fallback: see `statusTone` below.
export const STATUS_TONES = {
    // approvals / appeals. An approved appeal is not done yet: core-cron applies
    // it to its transaction within the minute, and only then is it executed or
    // failed (failed is keyed once, under callbacks).
    pending: 'needsAction',
    approved: 'inFlight',
    accepted: 'done',
    rejected: 'failed',
    refused: 'failed',
    executed: 'done',

    // payment instance lifecycle
    new: 'inFlight',
    initialize: 'inFlight',
    sum_blocked: 'needsAction',
    awaiting_3ds: 'needsAction',
    processed: 'done',
    cancelled: 'failed',
    awaiting_processing: 'needsAction',
    awaiting_withdraw: 'needsAction',
    withdrawed: 'done',
    withdraw_cancelled: 'failed',
    awaiting_confirmation: 'needsAction',

    // order lifecycle
    open: 'inFlight',
    payment_initiated: 'inFlight',
    payment_final: 'failed',
    payment_success: 'done',
    expired: 'failed',

    // callbacks
    init: 'inFlight',
    retry: 'needsAction',
    success: 'done',
    failed: 'failed',

    // gateway processing
    makepayment_error: 'failed',
    paymentcallback: 'inFlight',
    makepayment_awaiting_3ds: 'needsAction',
    makepayment_refused: 'failed',

    // cascading
    cascade_current: 'inFlight',
    cascade_cancelled: 'failed',
    cascade_final: 'done',

    ready: 'done',

    // telegram user sessions
    awaiting_code: 'needsAction',
    awaiting_password: 'needsAction',
    active: 'done',
    logged_out: 'neutral',
    error: 'failed',

    // telegram session messages. unmatched and ambiguous are money nobody has
    // been credited for yet, so they want an operator; ignored is a message no
    // income rule recognised, which is the normal fate of most of them.
    received: 'inFlight',
    unmatched: 'needsAction',
    ambiguous: 'needsAction',
    ignored: 'neutral',
    sent: 'done',

    // classifications, not lifecycle states
    primary: 'neutral',
    secondary: 'category',
    banned: 'failed',
    vip: 'done',
    white: 'done'
};

/**
 * Resolve a status string to its tone descriptor.
 *
 * Unrecognised statuses fall back to `neutral`. This is the one intentional
 * change of meaning: the previous fallback was the brand colour, so a status
 * nobody had classified rendered as a confident blue badge that looked exactly
 * like a deliberate one. Grey says "we have nothing to tell you about this",
 * which is the truth.
 */
export const statusTone = (status) =>
    TONES[STATUS_TONES[status]] || TONES.neutral;

/** Props to spread onto <Label> for a given status. */
export const statusLabelProps = (status) => {
    const { color, dot, variant } = statusTone(status);
    return variant ? { color, dot, variant } : { color, dot };
};
